import React, { useMemo, useState, useRef } from 'react';
import { message } from 'antd';
import { useThrottleEffect } from 'ahooks';
import BuyForm from './BuyForm';
import css from './index.module.scss';
import { EcoState, useDispatch, useStore } from '@/components/EcoCredits/context';
import Coupon from '@/components/EcoCredits/Coupon';
import NoteModal from '@/components/EcoCredits/ModalComp/NoteModal';
import { copyText } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
import Button from '@/components/UI/Button';
import EfModal from '@/components/UI/Modal';
import { EFArrow } from '@/components/UI/svg/Search';
import { rewardRedeemCoupon } from '@/service';
import { signupCookie } from '@/utils/cookies/signupCookie';

type ITabs = {
    key: keyof EcoState;
    label: string;
    desc: string;
    showAll: boolean;
};

interface props {
    isLogin: boolean;
    rewardUserInfo: any;
    refershData?: () => void;
    setShowPage: (modal: string) => void;
    pathUrl?: string;
}

const MaxCoupon = 4;
const EcoCreditRules: React.FC<props> = ({ rewardUserInfo, refershData, isLogin, setShowPage, pathUrl }) => {
    const { useRouter, useLang, isMobile } = useGlobal();
    const { t } = useLang('ecocredits');
    const state = useStore();
    const dispatch = useDispatch();
    const buyFormRef = useRef<any>(null);
    const router = useRouter();
    const { locale, asPath } = router;

    // 各种弹框
    const [couponModalOpen, setCouponModalOpen] = useState(false);
    const [currCoupon, setCurrCoupon] = useState<any>({});
    const [currRules, setCurrRules] = useState<any>(null);
    const [reedingLoading, setReedingLoading] = useState(false);
    const [currNotes, setCurrNotes] = useState<any>(null);

    const [allTabs, setAllTabs] = useState<ITabs[]>([
        { key: 'COUPONS_RULES_URL', label: t('coupons_rules_title'), desc: t('coupons_rules_desc'), showAll: false },
        { key: 'FREE_PRODUCTS_RULES_URL', label: t('product_rules_title'), desc: t('product_rules_desc'), showAll: false },
        { key: 'THIRD_COUPONS_RULES_URL', label: t('gift_card_title'), desc: '', showAll: false },
    ]);

    const errorMsg: Record<string, string> = {
        '2418': t('no_enough_points'), // 积分不足
        '2422': t('stay_too_long'), // 前端页面长时间不刷新，导致兑换规则落后后端版本
        '2423': t('exceeded_counts'), // 兑换次数超出上限
        '2424': t('stay_too_long'), // 前端页面长时间不刷新，导致优惠券规则超过截止时间
        '2429': t('no_stock_tip'), // 库存不足
        '2426': t('no_stock_tip'), // 库存不足
    };

    const filterTabs = useMemo(() => {
        return allTabs?.filter((tab) => {
            return state[tab.key]?.length;
        });
    }, [state]);

    const handleViewMore = (key: string) => {
        const target = allTabs.find((item) => item.key === key);
        if (target) target.showAll = !target?.showAll;
        setAllTabs([...allTabs]);
    };

    const successBtnMap: any = {
        COUPONS: t('store'),
        THIRD_COUPONS: t('spend_now'),
    };

    const openConfirmModal = async (rule: any) => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: t('redeem_ecocredits_title'),
                entry_position: 4,
            });
            router.push(`/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}#ECOCREDIT_RULES`)}`);
            return;
        }
        setCurrRules(rule);
    };

    const onConfirm = async () => {
        await onRedeem(currRules);
        setCurrRules(null);
    };

    const onRedeem = async (rule: any) => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: t('redeem_ecocredits_title'),
                entry_position: 4,
            });
            router.push(`/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}#ECOCREDIT_RULES`)}`);
            return;
        }

        const { ruleId, version, ruleWebsite, ruleType } = rule;
        const ruleSiteVersion = ruleWebsite?.version;
        const params: any = { ruleId, ruleVersion: version, ruleSiteVersion };

        // 如果是campaign规则 加上campaignId
        if (rule?.campaignId) {
            params.campaignVersion = rule?.campaignVersion;
        }

        // 如果是实体产品 加上地址
        if (ruleType === 'FREE_PRODUCTS') {
            const address = await buyFormRef?.current?.getFormData();
            params.address = address;
        }
        if (reedingLoading) return;
        setReedingLoading(true);

        try {
            const res = (await rewardRedeemCoupon(params, locale)) || '';
            if (res?.code === '0') {
                const data = res?.data || {};
                setCouponModalOpen(true);
                setCurrCoupon({ ...rule, ...data });
                copyText(res?.data?.coupon || '');
                refershData && refershData();
                dispatch({
                    type: 'getCouponsRules',
                    payload: { t, locale, pathUrl: pathUrl },
                });
                dispatch({
                    type: 'getProductRules',
                    payload: { locale, pathUrl },
                });
                dispatch({
                    type: 'getThiredCouponRules',
                    payload: { locale, pathUrl },
                });
                return;
            }

            message.error(errorMsg[res?.code] || res?.message || t('network_error') || 'Network Error');
        } finally {
            setReedingLoading(false);
        }
    };

    const shopNow = (coupon: any) => {
        const { thirdUrl, ruleType, jumpUrl } = coupon;
        // 第三方
        if (ruleType === 'THIRD_COUPONS') {
            if (isMobile) {
                router.push(thirdUrl);
                return;
            }
            setTimeout(() => window.open(thirdUrl, '_blank'));
            return;
        }

        // 普通优惠券并且填了jumpUrl
        if (ruleType === 'COUPONS' && jumpUrl) {
            if (isMobile) {
                router.push(jumpUrl);
                return;
            }
            setTimeout(() => window.open(jumpUrl, '_blank'));
            return;
        }

        // 默认
        if (isMobile) {
            router.push(`https://${locale}.ecoflow.com`);
            return;
        }
        window.open(`https://${locale}.ecoflow.com`, '_blank');
    };

    const getShowBtn = (coupon: any) => {
        const { ruleType, thirdUrl } = coupon || {};

        if (ruleType === 'COUPONS') return true;

        if (ruleType === 'THIRD_COUPONS' && thirdUrl) return true;

        return false;
    };

    useThrottleEffect(() => {
        dispatch({
            type: 'getCouponsRules',
            payload: { t, locale, pathUrl },
        });
        dispatch({
            type: 'getProductRules',
            payload: { locale, pathUrl },
        });
        dispatch({
            type: 'getThiredCouponRules',
            payload: { locale, pathUrl },
        });
    }, []);

    if (!filterTabs?.length) return <div></div>;

    return (
        <>
            <div className={`${css.ecocredit_rules}`}>
                <h5 id="FOR-REDEEM-NOW" className={`credits-title ${isLogin ? 'login' : 'unLogin'}`}>
                    {t('redeem_ecocredits_title')}
                </h5>
                <p className="credits-subtitle">{t('redeem_ecocredits_subtitle')}</p>
                <div className="container-center">
                    <div className="credits-desc-container">
                        <span className="credits-desc">
                            {isLogin &&
                                t.rich('have_points', {
                                    code: (children: string) => <i className="points">{children}</i>,
                                    points: rewardUserInfo?.points || '0',
                                })}
                        </span>
                    </div>
                </div>
                <div className="rule-content-box">
                    {allTabs.map((item) => {
                        if (!state[item.key] || state[item.key].length === 0) return <>{state[item.key]}</>;
                        return (
                            <div className="rule-content" key={item.key}>
                                <h6 className="rule-content-title">
                                    <span>{item.label}</span>
                                </h6>
                                <p className="rule-content-sutitle">{item.desc}</p>
                                <div className="rule-list-wrap">
                                    <ul className="rule-list">
                                        {state[item.key]
                                            .filter((_couponItem: any, couponIndex: number) => (item.showAll ? true : couponIndex < MaxCoupon))
                                            .map((rule: any) => (
                                                <Coupon
                                                    disabledBtn={
                                                        (isLogin && Number(rule?.points) > rewardUserInfo?.points) ||
                                                        (rule?.ruleType !== 'COUPONS' && rule?.stock === 0)
                                                    }
                                                    showstock={rule?.ruleType !== 'COUPONS' && rule?.stockPicEnable}
                                                    className="rule-item"
                                                    key={rule?.ruleId}
                                                    coupon={rule}
                                                    onViewsRules={setCurrNotes}
                                                    onReedem={openConfirmModal}
                                                    showCountDown
                                                    hideAmount={!['COUPONS', 'GIFT_CARD']?.includes(rule?.ruleType)}
                                                />
                                            ))}
                                    </ul>
                                </div>
                                {state[item.key].length > MaxCoupon && (
                                    <div className="view_more" onClick={() => handleViewMore(item.key)}>
                                        {item.showAll ? (
                                            <>
                                                {t('view_less')} <EFArrow className="view_less" />
                                            </>
                                        ) : (
                                            <>
                                                {t('view_more')} <EFArrow />
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 兑换成功弹框 */}
            <EfModal
                className={`${css.efmodal} coupon`}
                open={couponModalOpen}
                visible={couponModalOpen}
                onCancel={() => setCouponModalOpen(false)}
                zIndex={1200}
            >
                <div className="content">
                    <Coupon
                        className="coupon-card"
                        coupon={currCoupon}
                        onViewsRules={setCurrNotes}
                        hideAmount={!['COUPONS', 'GIFT_CARD']?.includes(currCoupon?.ruleType)}
                        showJumpBtn={'THIRD_COUPONS' === currCoupon?.ruleType && currCoupon.useUrl}
                        hideBtn
                        hideNote
                    />
                    <p className="m-desc">{t('redeemed_succ')}</p>
                    {['FREE_PRODUCTS', 'THIRD_COUPONS']?.includes(currCoupon?.ruleType) ? (
                        <p className="m-third-desc">
                            {currCoupon?.ruleType === 'THIRD_COUPONS' && `${t('copy_desc_2')}, `}
                            <br />
                            {t.rich('grab_success_text2', {
                                code: (children: string) => (
                                    <i
                                        className="btn"
                                        onClick={() => {
                                            setCouponModalOpen(false);
                                            setShowPage('coupon_modal$1');
                                            setCurrCoupon(undefined);
                                        }}
                                    >
                                        {children}
                                    </i>
                                ),
                            })}
                        </p>
                    ) : (
                        <p className="m-desc end">{t('copy_desc')}</p>
                    )}

                    {getShowBtn(currCoupon) && (
                        <Button onClick={() => shopNow(currCoupon)} className="m-btn">
                            {successBtnMap[currCoupon?.ruleType] || t('store')}
                        </Button>
                    )}
                </div>
            </EfModal>

            {/* 兑换确认弹框 */}
            <EfModal className={`${css.efmodal} coupon over-height`} open={!!currRules} visible={!!currRules} onCancel={() => setCurrRules(null)} zIndex={1200}>
                <div className="content">
                    <div className="prompt_icon">
                        <Coupon
                            className="coupon-card"
                            coupon={currRules}
                            hideAmount={!['COUPONS', 'GIFT_CARD']?.includes(currRules?.ruleType)}
                            hideBtn
                            hideNote
                        />
                    </div>
                    <div className="confirm-rule-content">
                        <h5 className="confirm-rule-title">{t('rules')}</h5>
                        <ul className="confirm-rule-list">
                            {currRules?.noteList?.map((note: string, index: number) => (
                                <li key={index} className="confirm-rule-item">
                                    {note}
                                </li>
                            ))}
                        </ul>
                    </div>
                    {currRules?.ruleType === 'FREE_PRODUCTS' && <BuyForm ref={buyFormRef} />}
                    <h5 className="m-prompt-title">{t('confirm_text')}</h5>
                    <Button loading={reedingLoading} onClick={onConfirm} className="m-btn">
                        {t('confirm_btn')}
                    </Button>
                </div>
            </EfModal>

            {/* 优惠券rules弹框 */}
            <NoteModal currNotes={currNotes} setCurrNotes={setCurrNotes} />
        </>
    );
};

export default EcoCreditRules;
